import { AreaViewIcon, makeStyles, SurfaceViewIcon, Typography, useTranslation } from '@paintscout/ui'
import type { MuiTheme as Theme } from '@paintscout/ui'
import { useClientOptions } from '@paintscout/ui-business'
import type { QuoteItemSection, QuoteItemView, RenderableItem, WorkOrderLayoutView } from '@paintscout/util/builder'
import {
  applyWorkOrderCardOrder,
  getItems,
  getQuoteOptions,
  getWorkOrderCardLayouts,
  setLineItemHiddenOnWorkOrder,
  setWorkOrderCardLayout,
  setWorkOrderCardOrder
} from '@paintscout/util/builder'
import DragHandleIcon from '@material-ui/icons/DragHandle'
import classnames from 'classnames'
import type { WorkOrderCardLayout } from 'paintscout'
import React, { useMemo } from 'react'
import type { SortableContainerProps, SortableElementProps } from 'react-sortable-hoc'
import { arrayMove, SortableContainer, SortableElement, SortableHandle } from 'react-sortable-hoc'
import { useQuote } from '../context'
import WorkOrderCardNext from './WorkOrderCardNext'

// Drag handle (react-sortable-hoc) — the icon is styled by the card's `dragHandleWrap`.
const DragHandle = SortableHandle(() => <DragHandleIcon />)

interface SortableCardProps {
  item: RenderableItem
  isFull?: boolean
  showHours?: boolean
  showTotalHours?: boolean
  gridItemClass: string
  gridItemFullClass: string
  onCardClick?: (ev: React.MouseEvent<HTMLElement>) => void
  onToggleHide?: () => void
  onToggleWidth?: () => void
}

const SortableCard: React.ComponentClass<SortableCardProps & SortableElementProps> = SortableElement(
  function SortableCardInner(props: SortableCardProps) {
    const {
      item,
      isFull,
      gridItemClass,
      gridItemFullClass,
      showHours,
      showTotalHours,
      onCardClick,
      onToggleHide,
      onToggleWidth
    } = props
    return (
      <div className={classnames(gridItemClass, { [gridItemFullClass]: isFull })}>
        <WorkOrderCardNext
          item={item}
          showHours={showHours}
          showTotalHours={showTotalHours}
          dragHandle={<DragHandle />}
          isFullWidth={isFull}
          onToggleHide={onToggleHide}
          onToggleWidth={onToggleWidth}
          onClick={onCardClick}
        />
      </div>
    )
  }
)

interface SortableGridProps {
  className: string
  children: React.ReactNode
}

const SortableGrid: React.ComponentClass<SortableGridProps & SortableContainerProps> = SortableContainer(
  function SortableGridInner({ className, children }: SortableGridProps) {
    return <div className={className}>{children}</div>
  }
)

export interface WorkOrderSectionNextProps {
  /** Section heading (e.g. "Areas" / "Surfaces"). */
  title: string
  /** Which item view drives the grid — area cards vs surface (substrate) cards. */
  tableView: QuoteItemView
  section?: QuoteItemSection
  showHours?: boolean
  showTotalHours?: boolean
  /** Show the section title (icon + heading). Off when the top nav pills already label the section. */
  showTitle?: boolean
  onItemClick?: (ev: React.MouseEvent<HTMLElement>, key: string) => void
}

const useStyles = makeStyles(
  (theme: Theme) => {
    const neutral = theme.paintscout.palette.brand.Neutral
    return {
      root: {},
      header: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: theme.spacing(2),
        marginBottom: theme.spacing(1.5)
      },
      titleLeft: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        minWidth: 0
      },
      titleIcon: {
        fontSize: 18,
        color: neutral.P60
      },
      // No prototype counterpart (its pills are the only section label), so this follows the card
      // headers: 16px medium in the same title color.
      title: {
        fontWeight: 500,
        fontSize: theme.typography.pxToRem(16),
        color: neutral.P35
      },
      grid: {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        // Dense auto-flow packs the (mixed half/full-width) cards to the least vertical height.
        gridAutoFlow: 'row dense',
        gap: theme.spacing(2.5),
        alignItems: 'start',
        [theme.breakpoints.down('sm')]: {
          gridTemplateColumns: '1fr'
        }
      },
      gridItem: {
        minWidth: 0
      },
      gridItemFull: {
        gridColumn: '1 / -1'
      },
      // The floating clone while dragging (react-sortable-hoc renders it detached from the grid).
      dragHelper: {
        zIndex: 2_147_483_002,
        cursor: 'grabbing',
        // Matches the card's own outer radius so the floating clone looks like the card it came from.
        borderRadius: theme.borderRadius.xl,
        boxShadow: theme.shadows[6],
        listStyle: 'none'
      }
    }
  },
  { name: 'WorkOrderSectionNext' }
)

/**
 * A Work Order section (Areas / Surfaces) in the redesigned estimateNext style: an icon + title with
 * "Clean up" + "Show all hours" controls, a dense-packed grid of draggable {@link WorkOrderCardNext}
 * cards, and a collapsible "Hidden {title} (N)" panel with Restore. Each card's hover controls hide
 * it (into the panel) or toggle its width (full-width spans both columns); dragging reorders the
 * cards. Hidden/width/order state persists on `quote.workOrderLayout`, scoped to this view and to the
 * work order — none of it touches the estimate's item order.
 */
export default function WorkOrderSectionNext({
  title,
  tableView,
  section = 'bid',
  showHours,
  showTotalHours,
  showTitle = true,
  onItemClick
}: WorkOrderSectionNextProps) {
  const classes = useStyles()
  const { quote, isEditable, updateQuote } = useQuote()
  const { options } = useClientOptions()
  const { lang } = useTranslation()
  const quoteOptions = getQuoteOptions({ options, quote })

  // Per-view layout persisted on the quote: per-card overrides (`cards`) + the work order's own card
  // order (`order`), both scoped to the work order and never applied to the estimate.
  const viewKey: WorkOrderLayoutView = tableView === 'substrate' ? 'substrate' : 'area'

  const items = useMemo(() => {
    let items = getItems({
      quote,
      section,
      view: tableView,
      consumer: 'crew',
      options,
      overrideOptions: { showQuantities: true },
      showLineItems: true,
      showValues: showHours,
      hideDimensions: quoteOptions.hideWorkOrderDimensions,
      lang
    })
    if (section === 'bid') {
      const addedItems = getItems({
        quote,
        section: 'added-options',
        view: tableView,
        consumer: 'crew',
        options,
        overrideOptions: { showQuantities: true },
        showLineItems: true,
        showValues: showHours,
        hideDimensions: quoteOptions.hideWorkOrderDimensions,
        lang
      })
      items = [...items, ...addedItems]
    }
    // The work order's own card order (`quote.workOrderLayout[view].order`) is applied here rather
    // than written back to `quote.order`, so rearranging cards leaves the estimate alone (ENG-1433).
    return applyWorkOrderCardOrder(items, quote.workOrderLayout?.[viewKey]?.order)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quote, tableView, showHours])

  const layout = getWorkOrderCardLayouts(quote, viewKey)
  const visibleItems = items.filter((item) => !layout[item.key]?.hidden)

  if (!items.length) {
    return null
  }

  const patchLayout = (key: string, patch: WorkOrderCardLayout) =>
    updateQuote({ quote: setWorkOrderCardLayout({ quote, view: viewKey, key, patch }) })

  // Line items carry their own hide flag — the estimate's "Hidden on Work Order" checkbox — so their
  // cards write that instead of the per-view layout. One flag, both screens: ticking the checkbox on
  // the estimate drops the card here, and hiding the card here ticks the checkbox (ENG-1930).
  const handleToggleHide = (key: string) => {
    const item = items.find((i) => i.key === key)
    if (item?.type === 'line_item') {
      updateQuote({ quote: setLineItemHiddenOnWorkOrder({ quote, key, hidden: true }) })
      return
    }
    patchLayout(key, { hidden: !layout[key]?.hidden })
  }
  const handleToggleWidth = (key: string) =>
    patchLayout(key, { width: layout[key]?.width === 'full' ? undefined : 'full' })

  // Reorder the visible cards, rebuild the full ordered list keeping hidden cards in place, and save
  // it as this view's work-order card order. Only `quote.workOrderLayout` changes — the estimate's
  // item order stays where the estimator put it (ENG-1433).
  const handleSortEnd = ({ oldIndex, newIndex }: { oldIndex: number; newIndex: number }) => {
    if (oldIndex === newIndex) {
      return
    }
    const reorderedVisible = arrayMove(visibleItems, oldIndex, newIndex)
    let vi = 0
    const fullOrder = items.map((item) => (layout[item.key]?.hidden ? item : reorderedVisible[vi++]))
    updateQuote({
      quote: setWorkOrderCardOrder({ quote, view: viewKey, keys: fullOrder.map((item) => item.key) })
    })
  }

  return (
    <div className={classes.root}>
      {/* Title (icon + heading). Clean up for this view lives in the top nav bar, not here. */}
      {showTitle && (
        <div className={classes.header}>
          <div className={classes.titleLeft}>
            {tableView === 'substrate' ? (
              <SurfaceViewIcon className={classes.titleIcon} />
            ) : (
              <AreaViewIcon className={classes.titleIcon} />
            )}
            <Typography className={classes.title}>{title}</Typography>
          </div>
        </div>
      )}

      <SortableGrid
        className={classes.grid}
        axis="xy"
        useDragHandle
        useWindowAsScrollContainer
        helperClass={classes.dragHelper}
        onSortEnd={handleSortEnd}
      >
        {visibleItems.map((item: RenderableItem, index: number) => (
          <SortableCard
            key={item.key}
            index={index}
            item={item}
            isFull={layout[item.key]?.width === 'full'}
            showHours={showHours}
            showTotalHours={showTotalHours}
            gridItemClass={classes.gridItem}
            gridItemFullClass={classes.gridItemFull}
            onCardClick={onItemClick ? (ev) => onItemClick(ev, item.key) : undefined}
            onToggleHide={isEditable ? () => handleToggleHide(item.key) : undefined}
            onToggleWidth={isEditable ? () => handleToggleWidth(item.key) : undefined}
          />
        ))}
      </SortableGrid>
    </div>
  )
}
